import React, { useState } from "react";
import { Box, TextField, Typography } from "@mui/material";

import ConfirmDialog from "../../shared/ConfirmDialog";
import useDiscontinueGoal from "hooks/api/goals/useDiscontinueGoal";

export interface DiscontinueGoalPanelProps {
  /** Controls dialog visibility */
  open: boolean;
  /** Called when the dialog should close */
  onClose: () => void;
  /** Goal name shown in the description */
  goalName: string;
  /** ID of the goal to discontinue */
  goalId: number;
  /** Client ID for query invalidation */
  clientId: string;
}

/**
 * Confirmation dialog for discontinuing a goal.
 * Collects a required rationale that is stored with the status change
 * and included in future reassessment treatment plans.
 */
const DiscontinueGoalPanel: React.FC<DiscontinueGoalPanelProps> = ({
  open,
  onClose,
  goalName,
  goalId,
  clientId,
}) => {
  const [rationale, setRationale] = useState("");

  const { discontinueGoal, isDiscontinuingGoal } = useDiscontinueGoal({
    onSuccess: () => {
      onClose();
      setRationale("");
    },
  });

  const handleClose = () => {
    if (isDiscontinuingGoal) return;
    setRationale("");
    onClose();
  };

  return (
    <ConfirmDialog
      open={open}
      title="Discontinue Goal"
      confirmLabel="Discontinue"
      confirmColor="error"
      onClose={handleClose}
      onConfirm={() => discontinueGoal({ goalId, clientId, rationale })}
      isLoading={isDiscontinuingGoal}
      confirmDisabled={!rationale.trim()}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 5, py: 3 }}>
        <Typography variant="body2">
          &ldquo;{goalName}&rdquo; &mdash; This goal will be marked as discontinued. This indicates
          the goal is no longer being pursued.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="subtitle2">What is your rationale for this action?</Typography>
          <TextField
            multiline
            rows={5}
            fullWidth
            placeholder="Enter your rationale..."
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            aria-label="Rationale for discontinuing goal"
          />
          <Typography variant="caption">
            This will be included in future reassessment treatment plans.
          </Typography>
        </Box>
      </Box>
    </ConfirmDialog>
  );
};

export default DiscontinueGoalPanel;
